import { motion } from "framer-motion";
import {
  MailCheck,
  FileSpreadsheet,
  Sprout,
  ShieldCheck,
  Clock4,
  TrendingUp,
} from "lucide-react";

const ITEMS = [
  {
    icon: MailCheck,
    title: "Monthly tax position emails",
    body: "No more year-end shocks. After each month closes, we read your ledger and email a clean estimate of tax owing — every month, without fail.",
    span: "md:col-span-7 md:row-span-2",
    accent: true,
  },
  {
    icon: FileSpreadsheet,
    title: "The right tool for your entity",
    body: "Companies stay in Xero — our certified advisors keep the file clean. Sole traders use Prosaic (a simpler alternative built for one-person operators) which can also raise simple invoices for you, though it isn't a full accounts-receivable system.",
    span: "md:col-span-5",
  },
  {
    icon: Sprout,
    title: "Sole trader start-ups welcome",
    body: "Just getting going? No Xero file, no accountant, no worries. We'll set you up on Prosaic and keep tax on track while you grow.",
    span: "md:col-span-5",
  },
  {
    icon: ShieldCheck,
    title: "Fully remote, IRD-registered",
    body: "We're a fully remote NZ tax practice — registered office in Te Atatu, Auckland. IRD-registered tax agents handling extensions and correspondence on your behalf, wherever you are in NZ.",
    span: "md:col-span-4",
  },
  {
    icon: Clock4,
    title: "Fast, flat pricing",
    body: "Fixed monthly subscriptions. No surprise invoices. Cancel any time.",
    span: "md:col-span-4",
  },
  {
    icon: TrendingUp,
    title: "Planning, not just filing",
    body: "Quarterly or monthly strategy calls depending on your plan — so tax becomes a decision, not a bill.",
    span: "md:col-span-4",
  },
];

export default function Features() {
  return (
    <section id="features" className="py-20 md:py-32" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-slate-500">Why NZ Taxation</div>
          <h2 className="mt-3 font-heading font-black text-4xl md:text-5xl tracking-tight leading-tight">
            A tax practice that reports to you,<br />
            not the other way around.
          </h2>
          <p className="mt-5 text-slate-600 text-lg max-w-2xl">
            We combine Xero, great bookkeeping, and a monthly email habit so you always know where you stand.
          </p>
        </div>

        <div className="mt-12 grid md:grid-cols-12 auto-rows-fr gap-4">
          {ITEMS.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.45, delay: i * 0.05 }}
                className={`group border border-slate-200 bg-white p-7 md:p-9 transition-transform hover:-translate-y-1 flex flex-col ${
                  item.span
                } ${item.accent ? "bg-[#0A192F] text-white border-[#0A192F]" : ""}`}
                data-testid={`feature-card-${i}`}
              >
                <Icon
                  className={`w-6 h-6 ${item.accent ? "text-[#C9A349]" : "text-[#0A192F] group-hover:text-[#C9A349] transition-colors"}`}
                />
                <h3
                  className={`mt-5 font-heading font-bold text-xl md:text-2xl tracking-tight ${
                    item.accent ? "text-white" : "text-[#0A192F]"
                  }`}
                >
                  {item.title}
                </h3>
                <p
                  className={`mt-3 leading-relaxed ${
                    item.accent ? "text-slate-300" : "text-slate-600"
                  }`}
                >
                  {item.body}
                </p>
                {item.accent && <InboxPreview />}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/**
 * Mini inbox preview shown inside the dark accent feature card so it doesn't
 * read as visually empty. Mimics a year of monthly tax-position emails.
 */
function InboxPreview() {
  const months = [
    { m: "Dec", amount: "$4,820.65", new: true, label: "for November" },
    { m: "Nov", amount: "$4,402.18", label: "for October" },
    { m: "Oct", amount: "$3,977.40", label: "for September" },
    { m: "Sep", amount: "$3,512.05", label: "for August" },
    { m: "Aug", amount: "$3,090.77", label: "for July" },
  ];
  return (
    <div className="mt-8 flex-1 flex flex-col justify-end">
      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#C9A349] mb-3">
        Inbox · last 5 months
      </div>
      <div className="border border-white/10 bg-white/[0.04] backdrop-blur-sm divide-y divide-white/10">
        {months.map((row, idx) => (
          <div
            key={row.m}
            className="flex items-center gap-4 px-4 py-3 hover:bg-white/[0.06] transition-colors"
          >
            <div className="w-1.5 h-1.5 bg-[#C9A349] rotate-45 flex-shrink-0" />
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-slate-400 w-12 flex-shrink-0">
              {row.m}
            </div>
            <div className="text-sm text-slate-200 truncate flex-1">
              Tax position {row.label} — {row.amount}
            </div>
            {row.new && (
              <div className="text-[9px] uppercase tracking-[0.22em] font-bold text-[#0A192F] bg-[#C9A349] px-1.5 py-0.5 flex-shrink-0">
                New
              </div>
            )}
            {idx === 0 && !row.new && (
              <div className="w-2 h-2 rounded-full bg-[#C9A349] flex-shrink-0" />
            )}
          </div>
        ))}
      </div>
      <div className="mt-3 text-[11px] text-slate-400 leading-relaxed">
        One short, plain-English email — sent shortly after each month closes. No surprises at
        year-end.
      </div>
    </div>
  );
}
